import {$getRoot, $isElementNode, RootNode, type LexicalEditor} from 'lexical';
import {$isRootTextContentEmptyCurry} from '@lexical/text';
import {useEffect, useLayoutEffect, useState} from 'react';
import { useSelector } from 'react-redux';
import { getAttachments } from '../ccf-inbox/ccf-inbox.slice';
import { AUDIO_FILE_TYPES } from '../ccf-attachment-container/ccf-fileupload';


/**
 * Custom hook to determine if the text content of a Lexical editor is empty.
 *
 * @param editor - The Lexical editor instance.
 * @param trim - Optional boolean to trim the text content.
 * @returns A boolean indicating if the text content is empty.
 * @example
 * const isEmpty = useLexicalIsTextContentEmpty(editor, true);
 */
export function useLexicalIsTextContentEmpty(
  editor: LexicalEditor,
  trim?: boolean,
): boolean {
  const [isEmpty, setIsEmpty] = useState(
    editor
      .getEditorState()
      .read($isRootTextContentEmptyCurry(editor.isComposing(), trim)),
  );

  const [isAudioAttachmentEmpty, setIsAudioAttachmentEmpty] = useState(true);
  const attachments = useSelector(getAttachments);

  useEffect(() => {
    attachments && attachments.length > 0 && attachments.filter(file=>AUDIO_FILE_TYPES.includes(file.fileType)).length > 0 ? setIsAudioAttachmentEmpty(false) : setIsAudioAttachmentEmpty(true);
  },[attachments])

  useLayoutEffect(() => {
    return editor.registerUpdateListener(({editorState}) => {
      const isComposing = editor.isComposing();
      let IsCurrentEditorEmpty = editorState.read(
        $isRootTextContentEmptyCurry(isComposing, trim),
      );
    if (IsCurrentEditorEmpty) {
      editorState.read(() => {
        IsCurrentEditorEmpty = !findInlineImage($getRoot());
        /**
         * Function to find if there is an inline image in the root node.
         *
         * @param root - The root node to search for inline images.
         * @returns A boolean indicating if an inline image is found.
         * @example
         */
        function findInlineImage(root: RootNode) {
          return root.getChildren().some((node) => {
            if ($isElementNode(node)) {
              return node
                .getChildren()
                .some((child) => child.getType() === 'inline-image');
            }
            return node.getType() === 'inline-image';
          });
        }
      });
    }
    isAudioAttachmentEmpty && setIsEmpty(IsCurrentEditorEmpty);
    });
  }, [editor, trim]);
  if (!isAudioAttachmentEmpty) {
    return false;
  }
  return isEmpty;
}